import moment from 'moment';
import { AllianceVsClientType, PromaBlocks, RiskTypes, ValueType } from '../items/itemDefinitions';

const getToday = () => moment(0, 'HH').toDate();

const getToday7 = () => moment(0, 'HH').add(7, 'd').toDate();

const getLast6M = () => moment(0, 'HH').subtract(6, 'months').toDate();

const initialCommonFilters = {
  photoTimeRange: [getLast6M(), getToday()],
  viewTime: getToday(),
  start: [null, null],
  due: [null, null]
};

const initialRiskFilters = {
  currentAss: '717', // null
  currentBlock: 'ALL',
  currentRiskTech: 'RISK',
  currentSimId: undefined,
  kvPhase: false,
  valueType: ValueType.MEDIAN,
  allianceVsClient: AllianceVsClientType.ALL,
  riskType: RiskTypes.COST
};

const initialPromaFilters = {
  projectLevelAchievements: false
};

const initialEventFilters = {
  eventsWithAttendees: true
};

export const initialFilters = {
  ...initialRiskFilters,
  ...initialEventFilters,
  ...initialCommonFilters,
  ...initialPromaFilters
};

export const filterAssessments = (assessments, filterDict) => {
  return assessments.filter((ass) => ass.id.includes(filterDict.currentAss));
};

export const filterKapaCalendars = (calendars) => {
  return calendars.filter((cal) => cal.id.includes('kalasatamastapasilaan'));
};

export const filterSimResultSheets = (simResultSheets, filterDict) => {
  if (filterDict.currentSimId === undefined) {
    return simResultSheets;
  }
  return simResultSheets.filter((sheet) => sheet.id === filterDict.currentSimId);
};

export const filterRBSRisks = (RBSRisks, filterDict) => {
  let filteredRisks = RBSRisks;
  if (filterDict.kvPhase === false) {
    filteredRisks = filteredRisks.filter(
      (risk) =>
        risk.projectPhase === 0 ||
        risk.projectPhase.some((phase) => ['TV', 'JV', 'introduction'].includes(phase))
    );
  }
  filteredRisks = filteredRisks.filter((risk) => {
    return risk.scope === '0' || risk[`${filterDict.currentBlock}_factor`] !== 0;
  });
  filteredRisks = filteredRisks.filter((risk) => {
    return risk.riskTypes.includes(filterDict.riskType);
  });
  filteredRisks = filteredRisks.filter((risk) => {
    if (risk.clientRisk === null) {
      return true;
    }
    if (filterDict.allianceVsClient === AllianceVsClientType.ALL) {
      return true;
    }
    if (filterDict.allianceVsClient === AllianceVsClientType.ALLIANCE && !risk.clientRisk) {
      return true;
    }
    if (filterDict.allianceVsClient === AllianceVsClientType.CLIENT && risk.clientRisk === true) {
      return true;
    }
    return false;
  });
  return filteredRisks;
};

export const filterControls = (controls) => controls.filter((ctrl) => ctrl.status !== 'Rejected');

// Note end of start date range includes + 24 h
const filterItemsByDatesCal = (items, filterDict, fStartProp, fEndProp, iStartProp, iEndProp) => {
  if (
    filterDict[fStartProp][0] === null &&
    filterDict[fStartProp][1] === null &&
    filterDict[fEndProp][0] === null &&
    filterDict[fEndProp][1] === null
  ) {
    return items;
  }
  const filteredItems = items
    .filter((item) => {
      if (
        !item[iEndProp] &&
        (filterDict[fEndProp][0] !== null || filterDict[fEndProp][1] !== null)
      ) {
        return false;
      }
      if (item[iEndProp] && filterDict[fEndProp][0] !== null) {
        if (moment(item[iEndProp]).isBefore(filterDict[fEndProp][0])) {
          return false;
        }
      }
      if (item[iEndProp] && filterDict[fEndProp][1] !== null) {
        if (moment(item[iEndProp]).isAfter(filterDict[fEndProp][1])) {
          return false;
        }
      }
      return true;
    })
    .filter((item) => {
      if (item[iStartProp] === undefined) {
        return false;
      }
      if (
        !item[iStartProp] &&
        (filterDict[fStartProp][0] !== null || filterDict[fStartProp][1] !== null)
      ) {
        return false;
      }
      if (item[iStartProp] && item[iStartProp].date && filterDict[fStartProp][0] !== null) {
        if (moment(item[iStartProp].date).isBefore(filterDict[fStartProp][0])) {
          return false;
        }
      }
      if (item[iStartProp] && item[iStartProp].date && filterDict[fStartProp][1] !== null) {
        if (moment(item[iStartProp].date).isAfter(moment(filterDict[fStartProp][1]).add(1, 'd'))) {
          return false;
        }
      }
      return true;
    });
  return filteredItems;
};

const filterItemsByDates = (items, filterDict, fStartProp, fEndProp, iStartProp, iEndProp) => {
  if (
    filterDict[fStartProp] &&
    filterDict[fStartProp][0] === null &&
    filterDict[fStartProp] &&
    filterDict[fStartProp][1] === null &&
    filterDict[fEndProp] &&
    filterDict[fEndProp][0] === null &&
    filterDict[fEndProp] &&
    filterDict[fEndProp][1] === null
  ) {
    return items;
  }
  const filteredItems = items
    .filter((item) => {
      if (iEndProp === undefined || fEndProp === undefined) {
        return true;
      }
      if (
        !item[iEndProp] &&
        (filterDict[fEndProp][0] !== null || filterDict[fEndProp][1] !== null)
      ) {
        return false;
      }
      if (item[iEndProp] && filterDict[fEndProp][0] !== null) {
        if (moment(item[iEndProp]).isBefore(filterDict[fEndProp][0])) {
          return false;
        }
      }
      if (item[iEndProp] && filterDict[fEndProp][1] !== null) {
        if (moment(item[iEndProp]).isAfter(filterDict[fEndProp][1])) {
          return false;
        }
      }
      return true;
    })
    .filter((item) => {
      if (iStartProp === undefined || fStartProp === undefined) {
        return true;
      }
      if (item[iStartProp] === undefined) {
        return false;
      }
      if (
        !item[iStartProp] &&
        (filterDict[fStartProp][0] !== null || filterDict[fStartProp][1] !== null)
      ) {
        return false;
      }
      if (item[iStartProp] && item[iStartProp] && filterDict[fStartProp][0] !== null) {
        if (moment(item[iStartProp]).isBefore(filterDict[fStartProp][0])) {
          return false;
        }
      }
      if (item[iStartProp] && item[iStartProp] && filterDict[fStartProp][1] !== null) {
        if (moment(item[iStartProp]).isAfter(moment(filterDict[fStartProp][1]).add(1, 'd'))) {
          return false;
        }
      }
      return true;
    });
  return filteredItems;
};

const filterItemsByParents = (items, filterItems) => {
  if (filterItems === undefined || filterItems.length < 1) {
    return items;
  }
  const bucketIds = filterItems.map((bucket) => bucket.id);
  const bucketFilteredItems = items.filter((item) =>
    item.parents.some((id) => bucketIds.includes(id))
  );
  return bucketFilteredItems;
};

export const filterEvents = ({ items, filterDict }) => {
  let filteredItems = items;
  if (filterDict.eventsWithAttendees) {
    filteredItems = filteredItems.filter((item) => item.attendees);
  }
  filteredItems = filterItemsByDatesCal(filteredItems, filterDict, 'start', 'due', 'start', 'due');
  filteredItems = [...new Set(filteredItems)];
  return filteredItems;
};

export const filterPhotos = (photos, filterDict, filterItems) => {
  let filteredItems = filterItemsByDates(
    photos,
    filterDict,
    'photoTimeRange',
    undefined,
    'time',
    undefined
  );
  filteredItems = filterItemsByParents(
    filteredItems.filter((item) => item.source === 'Työmaakuvat'),
    filterItems
  ).concat(photos.filter((item) => item.source === 'Virtuaalimalli'));
  return filteredItems;
};

export const filterGoals = (goals, filterDict) => {
  return goals.filter(
    (goal) => goal.block === PromaBlocks[filterDict.currentBlock] && goal.status !== 'Hylätty'
  );
};

export const filterMilestones = (milestones) => {
  return milestones.filter((milestone) => milestone.status !== 'Hylätty');
};

export const filterAchievements = (achievements, filterDict) => {
  return achievements.filter(
    (achievement) =>
      ((achievement.isProjectLevelAchievement &&
        PromaBlocks[filterDict.projectLevelAchievements]) ||
        (!achievement.isProjectLevelAchievement &&
          !PromaBlocks[filterDict.projectLevelAchievements])) &&
      achievement.status !== 'Hylätty'
  );
};

export const filterTasks = (tasks) => {
  return tasks.filter((task) => task.status !== 'Hylätty');
};
